<template>
  <section class="space-y-4">
    <header class="flex items-start justify-between gap-4">
      <div class="flex-1">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
          <i class="far fa-radar text-primary-500 mr-2" />
          Competitor Scans
        </h3>
        <p class="text-sm text-gray-500">
          Find competing businesses in the local area and add them as negative keywords.
        </p>
        <p class="text-xs text-gray-400 mt-1">
          A scan runs one Google Local search per service through SerpAPI (rate-limited).
          Results need <strong>review and approval</strong> before they become negative keywords.
        </p>
      </div>
      <div :class="hasInflightRun ? 'cursor-not-allowed' : ''">
        <button
          class="btn btn-primary btn-sm"
          v-tooltip="hasInflightRun ? 'A scan is already running. Wait for it to finish before starting a new one.' : 'Start a new competitor discovery scan.'"
          :disabled="hasInflightRun"
          @click="openConfig">
          <i class="far fa-magnifying-glass mr-1" />
          New Scan
        </button>
      </div>
    </header>

    <ScanProgress
      v-if="inflightRun"
      :scan-run-id="inflightRun.id"
      @completed="handleCompleted"
      @failed="handleFailed" />

    <ScanReview
      v-else-if="reviewingRunId"
      :location-id="locationId"
      :scan-run-id="reviewingRunId"
      :csrf-token="csrfToken"
      @results-changed="loadHistory"
      @close-review="reviewingRunId = null" />

    <div v-else-if="completedRunWithPending" class="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/20 p-3 flex items-center justify-between">
      <span class="text-sm text-amber-800 dark:text-amber-200">
        <i class="far fa-circle-exclamation mr-1" />
        {{ completedRunWithPending.pending_count }} pending results from your last scan.
      </span>
      <button class="btn btn-primary btn-sm" @click="reviewingRunId = completedRunWithPending.id">
        Review
      </button>
    </div>

    <div v-if="!inflightRun && !reviewingRunId && history.length === 0"
      class="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 p-8 text-center">
      <i class="far fa-radar text-3xl text-gray-300 dark:text-gray-600 mb-3" />
      <p class="text-sm font-medium text-gray-700 dark:text-gray-200">
        No scans yet
      </p>
      <p class="text-sm text-gray-500 mt-1 max-w-md mx-auto">
        Find competitors in your area and add their names as negative keywords so your ads
        don't show up against them. Click "New Scan" to get started.
      </p>
    </div>

    <div v-if="history.length > 0" class="text-sm">
      <h4 class="text-xs font-semibold text-gray-500 uppercase mb-2">
        Past scans
      </h4>
      <ul class="divide-y divide-gray-200 dark:divide-gray-700">
        <li v-for="run in history" :key="run.id" class="py-2 flex items-center justify-between">
          <div>
            <span class="capitalize font-medium">{{ run.status }}</span>
            <span class="text-gray-500"> · {{ run.query_count }} queries · {{ formatDate(run.created_at) }}</span>
          </div>
          <button v-if="run.status === 'completed'"
            v-tooltip="'Open the review table for this scan.'"
            class="text-primary-500 hover:text-primary-700" @click="reviewingRunId = run.id">
            View results
          </button>
        </li>
      </ul>
    </div>

    <UiSlidePanel ref="slidePanel" />
  </section>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import vTooltip from '~/javascript/directives/tooltip'
import ScanProgress from './scan_progress.vue'
import ScanReview from './scan_review.vue'
import ScanConfig from './scan_config.vue'
import { UiSlidePanel } from '~/javascript/components/ui/slide_panel'

const props = defineProps({
  locationId: { type: String, required: true },
  csrfToken: { type: String, required: true },
  availableServices: { type: Array, default: () => []},
  defaultLocation: { type: String, default: '' }
})

const slidePanel = ref(null)
const history = ref([])
const reviewingRunId = ref(null)

const inflightRun = computed(() => history.value.find((r) => ['pending', 'running'].includes(r.status)))
const hasInflightRun = computed(() => !!inflightRun.value)
const completedRunWithPending = computed(() => null) // populated after we add pending_count fetch — see note

const formatDate = (iso) => new Date(iso).toLocaleString()

const loadHistory = async() => {
  const res = await fetch(`/ajax/locations/${props.locationId}/competitor_scan_runs`, { headers: { Accept: 'application/json' } })
  if (res.ok) {
    const data = await res.json()
    history.value = data.scan_runs
  }
}

const openConfig = () => {
  slidePanel.value.open({
    component: ScanConfig,
    props: {
      locationId: props.locationId,
      csrfToken: props.csrfToken,
      availableServices: props.availableServices,
      defaultLocation: props.defaultLocation
    },
    events: { 'scan-started': loadHistory }
  })
}

const handleCompleted = () => {
  loadHistory()
  // Auto-open review for the just-completed run
  if (inflightRun.value) reviewingRunId.value = inflightRun.value.id
}

const handleFailed = () => {
  loadHistory()
}

onMounted(loadHistory)
</script>
